import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { REFUND_REASONS, TRefundReason } from '@const/orders';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiOrderService, apiRefundService, RefundApiError } from '@api';
import FlagIcon from '@components/common/flag_icon';
import { TOrder, TGetOrderById, TTransaction, TOrderDetail } from '@xeppt/xeppt-sdk/types';
import { ETransactionAction, ETransactionType } from '@xeppt/xeppt-sdk/types';
import type { TRefundEntry, TOrderDetailTopLevelFields } from '../../../../types/order_detail';
import { mergeOrderDetail } from '../../../../types/order_detail';
import { useDebounceValue } from 'usehooks-ts';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import Status from '@components/common/status';
import Skeleton from '@components/common/skeleton';
import Button from '@components/common/button';
import { showToast } from '@components/toast';
import { prettifyAmount, formatDate, downloadFileFromBlob } from '@utils/index';
import DevPageHeader from '../_shared/PageHeader';
import FilterDrawer, { type SortDir } from '../_shared/FilterDrawer';
import PurchaseDetailDrawer from '../_shared/PurchaseDetailDrawer';
import ReportProblemSubview from '../../../modals/global/report_problem_subview';
import styles from './styles.module.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderTab = 'details' | 'items' | 'transactions';

// Backend returns `id` UUID and fields SDK types omit (cast with `as`). Only
// fields ordersMgmtItem genuinely nests under `order` belong here — see
// src/types/order_detail.ts's file header for the sibling fields it does NOT
// nest, which must never be added back here.
type TOrderNested = TOrder & {
    id: string;
    currency?: string;
    environment?: string;
    customer_display_name?: string;
};

// Fully-populated order shape used everywhere below once the detail
// envelope's top-level refund/breakdown fields have been merged onto it via
// mergeOrderDetail() — see src/types/order_detail.ts for why those fields
// live at the detail-envelope level, not on the nested order, per the backend.
type TOrderWithId = TOrderNested & TOrderDetailTopLevelFields;

// order_details items now carry a `description` field (new backend field; SDK type lags).
type TOrderDetailWithDesc = TOrderDetail & { description?: string };

// Individual order transactions now carry a `points` field (loyalty points earned
// on that transaction); same lagging-SDK-type pattern as TOrderDetailWithDesc above.
type TOrderTransaction = TTransaction & { points?: number };

// Derived from apiRefundService.getRefundQuote return type — avoids importing the interface.
type TRefundQuote = Awaited<ReturnType<typeof apiRefundService.getRefundQuote>>;

// Raw shape of the GET /orders/{id} response, BEFORE mergeOrderDetail() has
// relocated its top-level refund/breakdown fields onto `order`. Never read
// order_breakdown/refunded_amount/remaining_amount/refunds directly off this
// type's `order` — they aren't there yet; they live on the envelope itself.
type TOrderDetailApiResponse = Omit<TGetOrderById, 'order'> &
    TOrderDetailTopLevelFields & { order: TOrderNested };

type TOrderDetailResult = Omit<TGetOrderById, 'order'> & { order: TOrderWithId };

// Casts the raw GET /orders/{id} response and merges its top-level
// order_breakdown/refunded_amount/remaining_amount/refunds onto `order`,
// ONCE, right at the API boundary — every consumer of `selectedDetail` below
// (OrderDetailModal, RefundModal, RefundDetailDrawer, PurchaseDetailDrawer)
// reads `order.*` straight off React state, bypassing OrderDetailModal's own
// destructure entirely, so the merge MUST happen here rather than inside any
// single render function. Exported for direct regression testing (same
// export-for-testability convention as buildMergedTxnRows below).
export function toMergedOrderDetail(raw: TGetOrderById): TOrderDetailResult {
    const rawDetail = raw as TOrderDetailApiResponse;
    return {
        order: mergeOrderDetail(rawDetail),
        order_details: rawDetail.order_details,
        transactions: rawDetail.transactions
    };
}

type FetchParams = {
    isNew?: boolean;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    statuses?: string[];
    sortDir?: SortDir;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

const ORDER_TABS: { label: string; value: OrderTab }[] = [
    { label: 'Details', value: 'details' },
    { label: 'Items', value: 'items' },
    { label: 'Transactions', value: 'transactions' }
];

// Currency code → ISO-3166-1 alpha-2 country code for react-world-flags.
const CURRENCY_FLAG_CODE: Record<string, string> = {
    CAD: 'CA',
    USD: 'US',
    EUR: 'EU',
    GBP: 'GB',
    AUD: 'AU',
    CHF: 'CH',
    JPY: 'JP',
    MXN: 'MX'
};

const ORDER_STATUS_CHIPS: { label: string; value: string }[] = [
    { label: 'Paid', value: 'succeeded' },
    { label: 'Pending', value: 'pending' },
    { label: 'Canceled', value: 'cancelled' },
    { label: 'Refunded', value: 'refunded' },
    { label: 'Partially refunded', value: 'partially_refunded' }
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function currencyFlagCode(currency: string): string | null {
    return CURRENCY_FLAG_CODE[currency.toUpperCase()] ?? null;
}

function addDatetime(date: string, time: string): string {
    return `${date}T${time}.000Z`;
}

// The backend's `page` response field is a pure echo of the request — never
// server-incremented — so trusting it (via a `data.page`-derived
// `currentPage` state) made infinite scroll stick on page 1 forever: every
// "load more" re-requested the exact page it had just received. The next
// page must instead be derived from how many rows are already loaded. A
// missing request (the initial mount fetch) or an explicit `isNew` (filter
// apply/retry/search) both mean "start over from page 1"; anything else is
// a genuine load-more continuation from the scroll observer. Exported so
// this fix is independently regression-tested.
export function isFreshPageRequest(req?: FetchParams): boolean {
    return !req || !!req.isNew;
}

// See isFreshPageRequest above — never derive the next page from the
// backend's echoed `page` field.
export function computeNextPage(req: FetchParams | undefined, loadedCount: number): number {
    return isFreshPageRequest(req) ? 1 : Math.floor(loadedCount / PAGE_SIZE) + 1;
}

function nameInitials(displayName: string): string {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
}

function handleInitials(handle: string): string {
    const clean = handle.replace(/^@/, '');
    if (!clean) return '??';
    const parts = clean.split(/[._-]/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase();
}

function orderStatusLabel(status: string): string {
    switch (status) {
        case 'succeeded': return 'Paid';
        case 'pending': return 'Pending';
        case 'cancelled': return 'Canceled';
        case 'refunded': return 'Refunded';
        case 'partially_refunded': return 'Partially refunded';
        case 'expired': return 'Expired';
        default: return status;
    }
}

function orderStatusVariant(status: string): 'success' | 'warning' | 'error' | 'gray' | 'blue' | 'lightblue' {
    switch (status) {
        case 'succeeded': return 'success';
        case 'pending': return 'warning';
        case 'cancelled':
        case 'expired': return 'error';
        case 'refunded': return 'blue';
        case 'partially_refunded': return 'lightblue';
        default: return 'gray';
    }
}

function refundStatusVariant(status: string): 'success' | 'warning' | 'error' | 'gray' | 'blue' | 'lightblue' {
    switch (status) {
        case 'succeeded': return 'success';
        case 'pending': return 'warning';
        case 'failed':
        case 'error': return 'error';
        default: return 'gray';
    }
}

function refundStatusLabel(status: string): string {
    switch (status) {
        case 'succeeded': return 'Approved';
        case 'pending': return 'Pending';
        case 'failed':
        case 'error': return 'Failed';
        default: return status;
    }
}

// Characters that Excel/Google Sheets treat as a formula trigger when they
// lead a cell's content. Order fields are attacker-influenceable (a customer
// display name, description, etc. could be set to "=cmd()", "+HYPERLINK(...)",
// "-2+3", or "@SUM(...)") — CSV/Excel formula injection, CWE-1236. A leading
// TAB, CR, or LF is also checked since some spreadsheet parsers still
// recognize the trigger character once leading whitespace/control characters
// are skipped, which would otherwise slip past a naive first-character check.
const CSV_FORMULA_TRIGGER_CHARS = new Set(['=', '+', '-', '@']);

function startsWithCsvFormulaTrigger(value: string): boolean {
    let i = 0;
    while (i < value.length && (value[i] === '\t' || value[i] === '\r' || value[i] === '\n')) {
        i += 1;
    }
    return i < value.length && CSV_FORMULA_TRIGGER_CHARS.has(value[i]);
}

// Escapes one CSV cell: neutralizes formula-injection triggers first (a
// leading apostrophe forces spreadsheet apps to render the value as literal
// text instead of evaluating it), then quotes the field — doubling any
// internal double-quotes — when it contains a comma, quote, or newline, per
// RFC 4180. Exported for direct regression testing (same export-for-
// testability convention as buildMergedTxnRows below).
export function escapeCsvField(raw: string): string {
    const value = startsWithCsvFormulaTrigger(raw) ? `'${raw}` : raw;
    return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

const CSV_COLUMNS = ['Order ID', 'Date', 'Customer', 'Customer ID', 'Environment', 'Status', 'Amount'];

// Builds a CSV export of the orders CURRENTLY LOADED in the list (the same
// `orders` state array the table renders) — a client-side export of what's
// on screen, not a server-side export of the full result set. Columns
// mirror the table's own fields (order id, date, customer handle,
// environment badge, status, amount) plus the customer's display name.
// Exported for direct regression testing (same export-for-testability
// convention as buildMergedTxnRows below).
export function ordersToCsv(orders: TOrderWithId[]): string {
    const rows = orders.map((order) => {
        const currency = order.currency ?? 'CAD';
        return [
            order.order_id,
            formatDate(order.created_at, 'DD MMM YYYY'),
            order.customer_display_name ?? '',
            order.customer_id ?? '',
            order.environment === 'test' ? 'Test' : 'Live',
            orderStatusLabel(order.status),
            `$${prettifyAmount(String(order.amount))} ${currency}`
        ]
            .map(escapeCsvField)
            .join(',');
    });
    return [CSV_COLUMNS.join(','), ...rows].join('\r\n');
}

// ─── Refund Modal ─────────────────────────────────────────────────────────────

interface RefundModalProps {
    order: TOrderWithId;
    onClose: () => void;
    onSuccess: () => void;
}

export const RefundModal = ({ order, onClose, onSuccess }: RefundModalProps) => {
    const currency = order.currency ?? 'CAD';
    const available = Number(order.remaining_amount ?? '0');
    const flagCode = currencyFlagCode(currency);
    const [amount, setAmount] = useState(available > 0 ? String(available) : '');
    const [debouncedAmount] = useDebounceValue(amount, 400);
    const [reason, setReason] = useState<TRefundReason | ''>('');
    // Free-text reason typed by the user when "Other" is selected — this is
    // what actually gets submitted as `reason` in that case, not the literal
    // string "Other" (mirrors RefundOrderSubview, the wallet-portal equivalent
    // of this same refund-creation flow).
    const [customReason, setCustomReason] = useState('');
    const [isReasonOpen, setIsReasonOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [quote, setQuote] = useState<TRefundQuote | null>(null);
    const [isQuoting, setIsQuoting] = useState(false);
    const idempotencyKeyRef = useRef(crypto.randomUUID());
    const reasonRef = useRef<HTMLDivElement>(null);
    const amountNum = parseFloat(amount);
    const exceedsAvailable = !isNaN(amountNum) && amountNum > available;
    const isAmountValid = !isNaN(amountNum) && amountNum > 0 && !exceedsAvailable;
    const isOtherReasonValid = reason !== 'Other' || customReason.trim() !== '';
    const isFormValid = isAmountValid && reason !== '' && isOtherReasonValid;
    const canSubmit = isFormValid && !isSubmitting && !isQuoting;
    // Close the reason dropdown when clicking outside it.
    useEffect(() => {
        if (!isReasonOpen) return;
        const handler = (e: MouseEvent) => {
            if (reasonRef.current && !reasonRef.current.contains(e.target as Node)) {
                setIsReasonOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isReasonOpen]);
    // Fetch a refund quote whenever the debounced amount changes.
    useEffect(() => {
        const num = parseFloat(debouncedAmount);
        if (isNaN(num) || num <= 0) {
            setQuote(null);
            return;
        }
        let cancelled = false;
        setIsQuoting(true);
        void apiRefundService.getRefundQuote(order.id, num)
            .then((q) => {
                if (!cancelled) setQuote(q);
            })
            .catch(() => {
                if (!cancelled) setQuote(null);
            })
            .finally(() => {
                if (!cancelled) setIsQuoting(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedAmount]);
    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        setErrorMsg('');
        // "Other" sends the user's typed text, never the literal word "Other".
        const resolvedReason = reason === 'Other' ? customReason.trim() : reason;
        try {
            await apiRefundService.refundOrder(order.id, {
                reason: resolvedReason,
                amount: amountNum,
                idempotency_key: idempotencyKeyRef.current
            });
        } catch (err) {
            const msg =
                err instanceof RefundApiError
                    ? (err.message || 'Refund failed. Please try again.')
                    : 'Refund failed. Please try again.';
            setErrorMsg(msg);
            setIsSubmitting(false);
            return;
        }
        try {
            onSuccess();
        } catch {
            setIsSubmitting(false);
        }
    };
    const limitsRows = [
        { label: 'Daily', period: quote?.limits?.daily ?? { used: 0, cap: 0 } },
        { label: 'Weekly', period: quote?.limits?.weekly ?? { used: 0, cap: 0 } },
        { label: 'Monthly', period: quote?.limits?.monthly ?? { used: 0, cap: 0 } }
    ];
    const showFee = !!quote && quote.estimated_fee > 0;
    // "Total you pay" only ever differs from the entered amount when a fee is
    // added on top of it — Figma's zero-fee quote state (node 19362:309446,
    // "Refund pop-up ... - Default") omits this row entirely, so mirror
    // showFee's condition instead of showing it for every resolved quote.
    const showTotal = showFee;
    const showPoints = !!quote && quote.points > 0;
    return (
        <div className={styles.rm_overlay} onClick={onClose}>
            <div className={styles.rm_modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.rm_header_area}>
                    <button
                        type="button"
                        className={styles.rm_close_btn}
                        onClick={onClose}
                        aria-label="Close refund modal">
                        <Icon name="close" width={24} height={24} />
                    </button>
                    <p className={styles.rm_title}>Refund</p>
                    <div className={styles.rm_header_divider} />
                </div>
                <div className={styles.rm_content}>
                    {(isQuoting || !!quote) && (
                        <div className={styles.rm_limits_card}>
                            <p className={styles.rm_limits_title}>Limits</p>
                            {isQuoting && !quote ? (
                                <p className={styles.rm_limits_loading}>Loading limits…</p>
                            ) : (
                                <div className={styles.rm_limits_rows}>
                                    {limitsRows.map(({ label, period }) => (
                                        <div key={label} className={styles.rm_limit_row}>
                                            <span className={styles.rm_limit_label}>{label}</span>
                                            <div className={styles.rm_limit_values}>
                                                {period.cap === 0 ? (
                                                    <span className={styles.rm_limit_no_limit}>No limit</span>
                                                ) : (
                                                    <>
                                                        <span className={styles.rm_limit_used}>
                                                            {prettifyAmount(Number(period.used).toFixed(2))} {currency}
                                                        </span>
                                                        <span className={styles.rm_limit_sep}>/</span>
                                                        <span className={styles.rm_limit_cap}>
                                                            {prettifyAmount(Number(period.cap).toFixed(2))} {currency}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className={styles.rm_available_card}>
                        <span className={styles.rm_available_label}>Available to refund:</span>
                        <span className={styles.rm_available_value}>
                            {' '}${prettifyAmount(Number(available).toFixed(2))} {currency}
                        </span>
                    </div>
                    <div className={styles.rm_inputs_section}>
                        <div>
                            <div className={styles.rm_field_label_row}>
                                <span className={styles.rm_field_label}>Refund amount</span>
                                <Info variant="right" content={<>Enter the amount to refund. Cannot exceed the available balance.</>} />
                            </div>
                            <div className={[
                                styles.rm_amount_box,
                                exceedsAvailable ? styles.rm_amount_box_error : ''
                            ].join(' ').trim()}>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className={styles.rm_amount_input}
                                    value={amount}
                                    placeholder="0.00"
                                    onChange={(e) => setAmount(e.target.value)}
                                    aria-label="Refund amount"
                                />
                                <div className={styles.rm_currency_suffix}>
                                    {flagCode && <FlagIcon name={flagCode} width={20} height={20} />}
                                    <span>{currency}</span>
                                </div>
                            </div>
                            {exceedsAvailable && (
                                <p className={styles.rm_amount_error}>
                                    Amount exceeds available balance
                                </p>
                            )}
                            {(showFee || showTotal) && (
                                <div className={styles.rm_fee_total_block}>
                                    {showFee && quote && (
                                        <div className={styles.rm_fee_row}>
                                            <span className={styles.rm_fee_label}>Estimated fees:</span>
                                            <span className={styles.rm_fee_value}>
                                                ${prettifyAmount(Number(quote.estimated_fee).toFixed(2))} {currency}
                                            </span>
                                            <Info
                                                variant="right"
                                                content={<>Processing fee for this refund. Calculated based on your current pricing plan.</>}>
                                                <button type="button" className={styles.rm_get_info_btn}>
                                                    <Icon name="info" width={18} height={18} />
                                                    Get info
                                                    <Icon name="nav_right" width={16} height={16} />
                                                </button>
                                            </Info>
                                        </div>
                                    )}
                                    {showTotal && quote && (
                                        <div className={styles.rm_total_row}>
                                            <span className={styles.rm_total_label}>Total you pay:</span>
                                            <span className={styles.rm_total_value}>
                                                {' '}${prettifyAmount(Number(quote.total).toFixed(2))} {currency}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {showPoints && quote && (
                            <div className={styles.rm_points_card}>
                                <Icon name="loyalty" width={20} height={20} className={styles.rm_points_icon} />
                                <span className={styles.rm_points_text}>
                                    On this transaction you will earn{' '}
                                    <strong className={styles.rm_points_value}>{quote.points} points</strong>
                                    <Info variant="right" content={<>Points are awarded on every successful refund transaction.</>} />
                                </span>
                            </div>
                        )}
                        <div>
                            <div className={styles.rm_field_label_row}>
                                <span className={styles.rm_field_label}>Refund reason</span>
                                <Info variant="right" content={<>Select the reason for this refund. This is recorded for your records and may be shared with the customer.</>} />
                            </div>
                            <div className={styles.rm_reason_wrapper} ref={reasonRef}>
                                <button
                                    type="button"
                                    className={[
                                        styles.rm_reason_select,
                                        isReasonOpen ? styles.rm_reason_open : ''
                                    ].join(' ').trim()}
                                    onClick={() => setIsReasonOpen((prev) => !prev)}
                                    aria-haspopup="listbox"
                                    aria-expanded={isReasonOpen}>
                                    <span className={reason ? styles.rm_reason_value : styles.rm_reason_placeholder}>
                                        {reason || 'Select a reason'}
                                    </span>
                                    <Icon
                                        name={isReasonOpen ? 'arrow_up' : 'arrow_down'}
                                        width={18}
                                        height={18}
                                        className={styles.rm_reason_chevron}
                                    />
                                </button>
                                {isReasonOpen && (
                                    <div className={styles.rm_reason_dropdown} role="listbox">
                                        {REFUND_REASONS.map((r) => (
                                            <div
                                                key={r}
                                                role="option"
                                                aria-selected={reason === r}
                                                className={[
                                                    styles.rm_reason_option,
                                                    reason === r ? styles.rm_reason_option_selected : ''
                                                ].join(' ').trim()}
                                                onClick={() => {
                                                    setReason(r);
                                                    setIsReasonOpen(false);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        setReason(r);
                                                        setIsReasonOpen(false);
                                                    }
                                                }}
                                                tabIndex={0}>
                                                {r}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {reason === 'Other' && (
                                <input
                                    type="text"
                                    className={styles.rm_custom_reason_input}
                                    value={customReason}
                                    placeholder="Describe the reason"
                                    onChange={(e) => setCustomReason(e.target.value)}
                                    aria-label="Custom refund reason"
                                />
                            )}
                        </div>
                    </div>
                    {errorMsg && (
                        <p className={styles.rm_error_msg}>{errorMsg}</p>
                    )}
                </div>
                <div className={styles.rm_footer}>
                    <button type="button" className={styles.rm_btn_cancel} onClick={onClose}>
                        Cancel
                    </button>
                    <Button
                        variant="gold"
                        size="medium"
                        disabled={!canSubmit}
                        isLoading={isSubmitting}
                        onClick={handleSubmit}>
                        Refund
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Order Detail Modal ───────────────────────────────────────────────────────

type TMergedTxnRow = {
    id: string;
    // Date, not string — matches TTransaction.created_at's real SDK type, so
    // the transaction side needs no conversion; only the refund side (a raw
    // JSON string on the locally-declared TRefundEntry) is parsed into one.
    created_at: Date;
    amountLabel: string;
    colorClass: string;
    onClick: () => void;
};

// Normalizes refund entries (nested under order.refunds) and top-level
// transactions into one row shape, then sorts them into a single
// chronological ledger — Figma's Order-detail Transactions tab shows one
// dated line-item list (oldest first: the original purchase, then each
// refund in the order it happened), not two stacked blocks. Refund rows keep
// their own onRefundDetailClick handler (opens RefundDetailDrawer);
// transaction rows keep onTransactionClick (opens PurchaseDetailDrawer) — the
// two drawers show different fields, so the row model preserves which
// handler each row dispatches to. No currency suffix on the line item itself
// (matches the pre-existing transaction rows and the Figma line-item shape —
// currency only appears in the summary table above).
//
// 2026-07-13 merchant-tabs redesign: the backend's `transactions` array is now
// itself unified — it already includes each refund's own wallet-transaction
// leg (Action=REFUND) alongside the charge leg, so the SAME refund event would
// otherwise appear TWICE (once via `refunds`, once via `transactions`).
// RefundDetailDrawer needs fields (reason, refund-specific status, points) that
// only live on `TRefundEntry` — not on a plain transaction row — so `refunds`
// is still the row source for refund events; `transactions` is filtered down
// to non-refund legs to avoid the duplicate.
export function buildMergedTxnRows(
    refunds: TRefundEntry[] | undefined,
    transactions: TOrderTransaction[] | undefined,
    onRefundDetailClick: (refund: TRefundEntry) => void,
    onTransactionClick: (txn: TOrderTransaction) => void
): TMergedTxnRow[] {
    const refundRows: TMergedTxnRow[] = (refunds ?? []).map((refund) => ({
        id: refund.id,
        created_at: new Date(refund.created_at),
        // Refunds are always shown as a minus in the default text color —
        // never red — per the Figma spec: "not red and not green".
        amountLabel: `−$${prettifyAmount(refund.amount)}`,
        colorClass: '',
        onClick: () => onRefundDetailClick(refund)
    }));
    const transactionRows: TMergedTxnRow[] = (transactions ?? [])
        .filter((txn) => txn.action !== ETransactionAction.REFUND)
        .map((txn) => {
            const isReceived = txn.type === ETransactionType.DEBIT;
            return {
                id: txn.id,
                // ISO string at runtime (SDK types it Date) — normalize so the
                // sort comparator's .getTime() runs on a real Date.
                created_at: new Date(txn.created_at),
                amountLabel: `${isReceived ? '+' : '-'}$${prettifyAmount(Number(txn.amount).toFixed(2))}`,
                colorClass: isReceived ? styles.txn_amount_credit : styles.txn_amount_debit,
                onClick: () => onTransactionClick(txn)
            };
        });
    return [...refundRows, ...transactionRows].sort(
        (a, b) => a.created_at.getTime() - b.created_at.getTime()
    );
}

interface OrderDetailModalProps {
    detail: TOrderDetailResult;
    refundLocked: boolean;
    onClose: () => void;
    onRefundClick: () => void;
    onRefundDetailClick: (refund: TRefundEntry) => void;
    onTransactionClick: (txn: TOrderTransaction) => void;
}

export const OrderDetailModal = ({
    detail,
    refundLocked,
    onClose,
    onRefundClick,
    onRefundDetailClick,
    onTransactionClick
}: OrderDetailModalProps) => {
    const [activeTab, setActiveTab] = useState<OrderTab>('details');
    const [showReport, setShowReport] = useState(false);
    const { order, order_details, transactions } = detail;
    const currency = order.currency ?? 'CAD';
    const initials = order.customer_display_name
        ? nameInitials(order.customer_display_name)
        : handleInitials(order.customer_id ?? '');
    const isRefunded = order.status === 'refunded' || order.status === 'partially_refunded';
    const breakdown = order.order_breakdown ?? null;
    // Refundable when the order is (partially) captured and has a remaining balance.
    // Test-mode orders are refundable too: the backend routes them through a
    // simulate path (records + order state move, no real wallet movement).
    const isRefundable =
        !refundLocked &&
        (order.status === 'succeeded' || order.status === 'partially_refunded') &&
        Number(order.remaining_amount ?? '0') > 0;
    const mergedTxnRows = buildMergedTxnRows(
        order.refunds,
        transactions as TOrderTransaction[] | undefined,
        onRefundDetailClick,
        onTransactionClick
    );
    return (
        <div className={styles.panel_overlay} onClick={onClose}>
            <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                {showReport ? (
                    <ReportProblemSubview
                        transactionRef={order.order_id}
                        onBack={() => setShowReport(false)}
                        onClose={onClose}
                    />
                ) : (
                    <>
                        <div className={styles.panel_header}>
                            <Typography variant="body2" weight="semibold" className={styles.panel_title}>Order details</Typography>
                            <button
                                type="button"
                                className={styles.panel_close}
                                onClick={onClose}
                                aria-label="Close">
                                <Icon name="close" width={18} height={18} />
                            </button>
                        </div>
                        <div className={styles.panel_body}>
                            <div className={styles.pd_user_card}>
                                <div className={styles.pd_recipient_info}>
                                    <div className={styles.pd_avatar_wrapper}>
                                        <div className={styles.pd_avatar}>{initials}</div>
                                    </div>
                                    {order.customer_display_name && (
                                        <Typography variant="body2" weight="semibold" className={styles.panel_name}>
                                            {order.customer_display_name}
                                        </Typography>
                                    )}
                                    <Typography variant="body4" className={styles.panel_handle}>
                                        @{order.customer_id}
                                    </Typography>
                                    <Typography variant="body4" className={styles.panel_xeppt_label}>
                                        XEPPT user
                                    </Typography>
                                </div>
                                <div className={styles.pd_pill_row}>
                                    <span className={styles.pd_pill_line} />
                                    <span className={styles.pd_pill}>Order #{order.order_id}</span>
                                    <span className={styles.pd_pill_line} />
                                </div>
                                <Typography variant="h4" weight="semibold" className={styles.panel_amount}>
                                    ${prettifyAmount(order.total)} {currency}
                                </Typography>
                                <Status variant={orderStatusVariant(order.status)}>
                                    {orderStatusLabel(order.status)}
                                </Status>
                                {order.environment === 'test' && (
                                    <span className={styles.panel_env_badge}>Test</span>
                                )}
                            </div>
                            <div className={styles.panel_tabs}>
                                {ORDER_TABS.map((tab) => (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        className={[
                                            styles.panel_tab,
                                            activeTab === tab.value ? styles.panel_tab_active : ''
                                        ].join(' ')}
                                        onClick={() => setActiveTab(tab.value)}>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            <div className={styles.panel_tab_body}>
                                {activeTab === 'details' && (
                                    <div className={[styles.detail_rows, styles.pd_rows].join(' ')}>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Date and time</span>
                                            <span className={styles.detail_value}>
                                                {formatDate(order.created_at, 'DD MMMM YYYY, HH:mm')}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Total amount</span>
                                            <span className={[styles.detail_value, styles.pd_value_bold].join(' ')}>
                                                ${prettifyAmount(order.total)} {currency}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Order ID</span>
                                            <span className={styles.detail_value}>
                                                #{order.order_id}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Description</span>
                                            <span className={styles.detail_value}>
                                                {order.description || '—'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'items' && (
                                    order_details.length > 0 ? (
                                        <div className={styles.items_list}>
                                            <div className={styles.items_header_row}>
                                                <span className={styles.items_header_item}>Item</span>
                                                <span className={styles.items_header_qty}>Qty</span>
                                                <span className={styles.items_header_price}>Price</span>
                                            </div>
                                            {(order_details as TOrderDetailWithDesc[]).map((item, idx) => (
                                                <div key={item.sku || idx} className={styles.item_row}>
                                                    <div className={styles.item_info}>
                                                        <p className={styles.item_title}>{item.title}</p>
                                                        {item.description && (
                                                            <p className={styles.item_desc}>{item.description}</p>
                                                        )}
                                                        <p className={styles.item_qty}>×{item.quantity}</p>
                                                    </div>
                                                    <p className={styles.item_price}>
                                                        ${prettifyAmount(String(item.price))}
                                                    </p>
                                                </div>
                                            ))}
                                            <div className={styles.items_subtotal_row}>
                                                <div className={styles.item_info}>
                                                    <p className={styles.items_subtotal_label}>Subtotal</p>
                                                </div>
                                                <p className={styles.items_subtotal_value}>
                                                    ${prettifyAmount(breakdown?.subtotal ?? order.total)}
                                                </p>
                                            </div>
                                            <div className={styles.items_subtotal_row}>
                                                <div className={styles.item_info}>
                                                    <p className={styles.items_subtotal_label}>Shipping</p>
                                                </div>
                                                <p className={styles.items_subtotal_value}>
                                                    {breakdown
                                                        ? (Number(breakdown.shipping) === 0 ? 'Free' : `$${prettifyAmount(breakdown.shipping)}`)
                                                        : 'Free'}
                                                </p>
                                            </div>
                                            {breakdown && (
                                                <div className={styles.items_subtotal_row}>
                                                    <div className={styles.item_info}>
                                                        <p className={styles.items_subtotal_label}>Fees</p>
                                                    </div>
                                                    <p className={styles.items_subtotal_value}>
                                                        ${prettifyAmount(breakdown.fees)}
                                                    </p>
                                                </div>
                                            )}
                                            <div className={[styles.items_subtotal_row, styles.items_total_row].join(' ')}>
                                                <div className={styles.item_info}>
                                                    <p className={styles.items_total_label}>Total</p>
                                                </div>
                                                <p className={styles.items_total_value}>
                                                    ${prettifyAmount(breakdown?.total ?? order.total)} {currency}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={styles.tab_empty}>
                                            <Typography variant="body4">No items on this order.</Typography>
                                        </div>
                                    )
                                )}
                                {activeTab === 'transactions' && (
                                    <>
                                        {isRefunded && order.refunds && order.refunds.length > 0 && (
                                            <>
                                                <div className={styles.refund_banner}>
                                                    <Typography variant="body4">
                                                        {order.refunds.length > 1 ? (
                                                            'This order has been refunded multiple times.'
                                                        ) : (
                                                            <>
                                                                {'This order was refunded on '}
                                                                <strong>
                                                                    {formatDate(order.refunds[0].created_at, 'DD MMMM YYYY')}
                                                                </strong>
                                                                {'.  '}
                                                                <span
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    className={styles.refund_banner_link}
                                                                    onClick={() => {
                                                                        const r = order.refunds?.[0];
                                                                        if (r) onRefundDetailClick(r);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            const r = order.refunds?.[0];
                                                                            if (r) onRefundDetailClick(r);
                                                                        }
                                                                    }}>
                                                                    View refund
                                                                </span>
                                                            </>
                                                        )}
                                                    </Typography>
                                                </div>
                                                <div className={styles.refund_summary}>
                                                    <div className={styles.refund_summary_row}>
                                                        <span className={styles.refund_summary_label}>
                                                            Original amount
                                                        </span>
                                                        <span className={styles.refund_summary_value}>
                                                            ${prettifyAmount(order.total)} {currency}
                                                        </span>
                                                    </div>
                                                    <div className={styles.refund_summary_row}>
                                                        <span className={styles.refund_summary_label}>
                                                            Refunded
                                                        </span>
                                                        <span className={styles.refund_summary_value}>
                                                            ${prettifyAmount(order.refunded_amount ?? '0')} {currency}
                                                        </span>
                                                    </div>
                                                    <div className={styles.refund_summary_row}>
                                                        <span className={styles.refund_summary_label}>
                                                            Remaining amount
                                                        </span>
                                                        <span className={styles.refund_summary_value}>
                                                            ${prettifyAmount(order.remaining_amount ?? '0')} {currency}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {/* One merged, dated line-item list (purchase + every
                                            refund), per the Figma spec, rather than two
                                            stacked blocks. Refund rows still open
                                            RefundDetailDrawer; transaction rows still open
                                            PurchaseDetailDrawer. */}
                                        {mergedTxnRows.length > 0 ? (
                                            <div className={styles.txn_list}>
                                                {mergedTxnRows.map((row) => (
                                                    <div
                                                        key={row.id}
                                                        className={[styles.txn_row, styles.txn_row_clickable].join(' ')}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={row.onClick}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') row.onClick();
                                                        }}>
                                                        <span className={styles.txn_date}>
                                                            {formatDate(row.created_at, 'DD MMM YYYY')}
                                                        </span>
                                                        <span
                                                            className={[styles.txn_amount, row.colorClass].join(' ')}>
                                                            {row.amountLabel}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className={styles.tab_empty}>
                                                <Typography variant="body4">
                                                    No transactions on this order.
                                                </Typography>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className={styles.panel_footer}>
                            <Button
                                variant="outlined-red"
                                className={styles.panel_footer_btn}
                                onClick={() => setShowReport(true)}>
                                Report problem
                            </Button>
                            <Button
                                variant="outlined-primary"
                                className={styles.panel_footer_btn}
                                disabled={!isRefundable}
                                onClick={isRefundable ? onRefundClick : undefined}>
                                Refund
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Refund Detail Drawer ─────────────────────────────────────────────────────

interface RefundDetailDrawerProps {
    refund: TRefundEntry;
    order: TOrderWithId;
    onClose: () => void;
    onViewOrder: () => void;
}

export const RefundDetailDrawer = ({ refund, order, onClose, onViewOrder }: RefundDetailDrawerProps) => {
    const [showReport, setShowReport] = useState(false);
    const currency = refund.currency ?? order.currency ?? 'CAD';
    const initials = order.customer_display_name
        ? nameInitials(order.customer_display_name)
        : handleInitials(order.customer_id ?? '');
    const totalCharged = (parseFloat(refund.amount) + parseFloat(refund.fee)).toFixed(2);
    return (
        <div className={styles.panel_overlay} onClick={onClose}>
            <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                {showReport ? (
                    <ReportProblemSubview
                        transactionRef={order.order_id}
                        isRefund
                        onBack={() => setShowReport(false)}
                        onClose={onClose}
                    />
                ) : (
                    <>
                        <div className={styles.panel_header}>
                            <Typography variant="body2" weight="semibold" className={styles.panel_title}>Transaction details</Typography>
                            <button
                                type="button"
                                className={styles.panel_close}
                                onClick={onClose}
                                aria-label="Close">
                                <Icon name="close" width={18} height={18} />
                            </button>
                        </div>
                        <div className={styles.panel_body}>
                            <div className={styles.rd_body_inner}>
                                <div className={styles.pd_user_card}>
                                    <div className={styles.pd_recipient_info}>
                                        <div className={styles.pd_avatar_wrapper}>
                                            <div className={styles.pd_avatar}>{initials}</div>
                                        </div>
                                        {order.customer_display_name && (
                                            <Typography variant="body2" weight="semibold" className={styles.panel_name}>
                                                {order.customer_display_name}
                                            </Typography>
                                        )}
                                        <Typography variant="body4" className={styles.panel_handle}>
                                            @{order.customer_id}
                                        </Typography>
                                        <Typography variant="body4" className={styles.panel_xeppt_label}>
                                            XEPPT user
                                        </Typography>
                                    </div>
                                    <div className={styles.pd_pill_row}>
                                        <span className={styles.pd_pill_line} />
                                        <span className={styles.pd_pill}>↑ Refund</span>
                                        <span className={styles.pd_pill_line} />
                                    </div>
                                    <Typography variant="h4" weight="semibold" className={styles.panel_amount}>
                                        −${prettifyAmount(totalCharged)} {currency}
                                    </Typography>
                                    <Status variant={refundStatusVariant(refund.status)}>
                                        {refundStatusLabel(refund.status)}
                                    </Status>
                                </div>
                                <div className={styles.rd_info_banner}>
                                    <span className={styles.rd_info_banner_icon}>
                                        <Icon name="info" width={16} height={16} />
                                    </span>
                                    <div className={styles.rd_info_banner_body}>
                                        <span className={styles.rd_info_banner_text}>
                                            {'This refund was issued for the order on '}
                                            <strong>{formatDate(order.created_at, 'DD MMMM YYYY')}</strong>
                                            {'.'}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.rd_info_banner_link}
                                            onClick={onViewOrder}>
                                            View order
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.rd_details_section}>
                                    <Typography variant="body2" weight="semibold" className={styles.rd_details_header}>
                                        Details
                                    </Typography>
                                    <div className={[styles.detail_rows, styles.pd_rows].join(' ')}>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Date and time</span>
                                            <span className={styles.detail_value}>
                                                {formatDate(refund.created_at, 'DD MMMM YYYY, HH:mm')}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Refund amount</span>
                                            <span className={[styles.detail_value, styles.pd_value_bold].join(' ')}>
                                                ${prettifyAmount(refund.amount)} {currency}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.rd_fee_label_cell}>
                                                Total fees (included)
                                                <Info variant="right" content={<>Processing fee included in this refund.</>} />
                                            </span>
                                            <span className={styles.detail_value}>
                                                ${prettifyAmount(refund.fee)} {currency}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Total charged</span>
                                            <span className={[styles.detail_value, styles.pd_value_bold].join(' ')}>
                                                ${prettifyAmount(totalCharged)} {currency}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Points you earned</span>
                                            <span className={styles.detail_value}>{refund.points}</span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Refund reason</span>
                                            <span className={styles.detail_value}>{refund.reason || '—'}</span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Original order ID</span>
                                            <span className={styles.detail_value}>
                                                #{order.order_id}
                                            </span>
                                        </div>
                                        <div className={styles.detail_row}>
                                            <span className={styles.detail_label}>Transaction ID</span>
                                            <span className={styles.detail_value}>
                                                {refund.id}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.panel_footer}>
                            <button
                                type="button"
                                className={styles.detail_report_btn}
                                onClick={() => setShowReport(true)}>
                                Report problem
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// PurchaseDetailDrawer now lives in ../_shared/PurchaseDetailDrawer.tsx so
// sales.tsx and payments/index.tsx can reuse it instead of the customer-facing
// VIEW_TRANSACTION modal — see the import above.

// ─── Layout ──────────────────────────────────────────────────────────────────

const DevelopersOrdersLayout = () => {
    const { user } = useUserContext();
    const { env } = useEnvironmentContext();

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch] = useDebounceValue(searchInput, 300);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusChips, setStatusChips] = useState<string[]>([]);
    const [sortDir, setSortDir] = useState<SortDir>('newest');

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState<TOrderDetailResult | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [isRefundOpen, setIsRefundOpen] = useState(false);
    const [refundJustSucceeded, setRefundJustSucceeded] = useState(false);
    const [selectedRefundEntry, setSelectedRefundEntry] = useState<TRefundEntry | null>(null);
    const [selectedTxnEntry, setSelectedTxnEntry] = useState<TOrderTransaction | null>(null);

    const [orders, setOrders] = useState<TOrderWithId[]>([]);
    const [trigger, setTrigger] = useState(0);
    const loader = useRef<HTMLDivElement>(null);
    const detailOpenRef = useRef(false);

    const {
        data: ordersResponse,
        isLoading,
        error,
        handleRequest: fetchOrders
    } = useApiQuery<FetchParams, {
        total?: number;
        total_success_orders: number;
        total_cancelled_orders: number;
        orders: TOrder[];
        page: number;
        page_size: number;
        pages_total: number;
    }>({
        method: (req?: FetchParams) => {
            const page = computeNextPage(req, orders.length);
            const orderVal = req?.sortDir === 'oldest' ? 'ASC' : 'DESC';
            return apiOrderService.findBusinessOrders({
                page,
                page_size: PAGE_SIZE,
                order: orderVal,
                search: req?.search || undefined,
                date_from: req?.dateFrom ? addDatetime(req.dateFrom, '00:00:00') : undefined,
                date_to: req?.dateTo ? addDatetime(req.dateTo, '23:59:59') : undefined,
                status:
                    req?.statuses && req.statuses.length > 0 ? req.statuses : undefined,
                environment: env
            }) as unknown as Promise<{
                total?: number;
                total_success_orders: number;
                total_cancelled_orders: number;
                orders: TOrder[];
                page: number;
                page_size: number;
                pages_total: number;
            }>;
        },
        onSuccess: (data, req) => {
            const incoming = (data.orders ?? []) as TOrderWithId[];
            if (isFreshPageRequest(req)) {
                setOrders(incoming);
            } else {
                setOrders((prev) => [...prev, ...incoming]);
            }
        },
        condition: !!user,
        deps: [user, env]
    });

    const applyFetch = useCallback(
        (overrides?: Partial<FetchParams>) => {
            fetchOrders({
                isNew: true,
                search: debouncedSearch,
                dateFrom,
                dateTo,
                statuses: statusChips,
                sortDir,
                ...overrides
            });
        },
        [fetchOrders, debouncedSearch, dateFrom, dateTo, statusChips, sortDir]
    );

    const openDetail = async (id: string) => {
        setIsLoadingDetail(true);
        try {
            const detail = await apiOrderService.getOrderById(id);
            setSelectedDetail(toMergedOrderDetail(detail));
            detailOpenRef.current = true;
        } catch {
            // silently fail — order may have been deleted
        } finally {
            setIsLoadingDetail(false);
        }
    };

    // After a successful refund: lock the Refund button immediately, then
    // re-fetch the order. Only unlock once fresh authoritative data arrives;
    // if the re-fetch fails, keep it locked and tell the user to reopen — this
    // prevents a stale drawer from minting a new key and issuing a 2nd refund.
    const handleRefundSuccess = async () => {
        const orderId = selectedDetail?.order.id;
        setRefundJustSucceeded(true);
        setIsRefundOpen(false);
        showToast.success('Refund issued successfully.');
        if (!orderId) return;
        try {
            const updated = await apiOrderService.getOrderById(orderId);
            if (!detailOpenRef.current) return;
            setSelectedDetail(toMergedOrderDetail(updated));
            setRefundJustSucceeded(false);
        } catch {
            if (detailOpenRef.current) {
                showToast.warning(
                    'Refund issued, but the order could not be refreshed. Close and reopen to continue.'
                );
            }
        }
    };

    useEffect(() => {
        if (debouncedSearch.length === 0 || debouncedSearch.length >= 3) {
            applyFetch({ search: debouncedSearch });
        }
    }, [debouncedSearch]);

    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) setTrigger((prev) => prev + 1);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '20px',
            threshold: 0
        });
        if (loader.current) observer.observe(loader.current);
    }, [handleObserver]);

    useEffect(() => {
        const total = ordersResponse?.total ?? 0;
        if (orders.length < total && !isLoading) {
            fetchOrders({ search: debouncedSearch, dateFrom, dateTo, statuses: statusChips, sortDir });
        }
    }, [trigger]);

    const handleFilterClose = () => {
        setIsFilterOpen(false);
        applyFetch();
    };

    const handleFilterReset = () => {
        setDateFrom('');
        setDateTo('');
        setStatusChips([]);
        setSortDir('newest');
    };

    const handleToggleStatus = (s: string) => {
        setStatusChips((prev) =>
            prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
        );
    };

    // Exports the orders CURRENTLY LOADED in the list (client-side only —
    // never fetches additional pages). The filename's date comes from the
    // newest loaded order, never from the system clock, so the export
    // stays deterministic and reflects the data it actually contains.
    const handleDownload = () => {
        if (orders.length === 0) return;
        const newest = orders.reduce(
            (latest, order) =>
                new Date(order.created_at).getTime() > new Date(latest.created_at).getTime()
                    ? order
                    : latest,
            orders[0]
        );
        const csv = ordersToCsv(orders);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        downloadFileFromBlob(blob, `orders-${formatDate(newest.created_at, 'YYYY-MM-DD')}.csv`);
    };

    const activeFilterCount =
        (dateFrom || dateTo ? 1 : 0) +
        (statusChips.length > 0 ? 1 : 0);

    const stats = [
        {
            title: 'Paid orders',
            helper: 'Number of successfully paid orders in the selected period',
            value: String(ordersResponse?.total_success_orders ?? 0),
            icon: 'check_outlined' as const,
            mod: styles.stat_success
        },
        {
            title: 'Canceled orders',
            helper: 'Number of orders cancelled before payment in the selected period',
            value: String(ordersResponse?.total_cancelled_orders ?? 0),
            icon: 'error_outlined' as const,
            mod: styles.stat_error
        },
        {
            title: 'Total orders',
            helper: 'Total number of orders created in the selected period',
            value: String(ordersResponse?.total ?? 0),
            icon: 'outlined_wallet' as const,
            mod: styles.stat_received
        }
    ];

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <DevPageHeader
                title="Orders"
                subtitle="View and reconcile orders placed through your XEPPT integration."
            />
            <div className={styles.stats}>
                {isLoading && !orders.length ? (
                    <>
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                        <Skeleton className={styles.stat_skeleton} />
                    </>
                ) : (
                    stats.map((item) => (
                        <div key={item.title} className={[styles.stat_card, item.mod].join(' ')}>
                            <div className={styles.stat_icon}>
                                <Icon name={item.icon} width={22} height={22} />
                            </div>
                            <div className={styles.stat_body}>
                                <div className={styles.stat_label_row}>
                                    <p className={styles.stat_label}>{item.title}</p>
                                    <Info variant="left" content={<>{item.helper}</>} />
                                </div>
                                <Typography variant="h5" weight="semibold" className={styles.stat_value}>
                                    {item.value}
                                </Typography>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div className={styles.list_card}>
                <div className={styles.toolbar}>
                    <div className={styles.search_wrap}>
                        <Icon name="search" width={16} height={16} className={styles.search_icon} />
                        <input
                            type="text"
                            placeholder="Search by order ID..."
                            value={searchInput}
                            className={styles.search_input}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                    <div className={styles.toolbar_actions}>
                        <button
                            type="button"
                            className={styles.download_btn}
                            disabled={orders.length === 0}
                            onClick={handleDownload}>
                            <Icon name="download" width={16} height={16} />
                            Download
                        </button>
                        <button
                            type="button"
                            className={styles.filters_btn}
                            onClick={() => setIsFilterOpen(true)}>
                            <Icon name="filter" width={16} height={16} />
                            {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
                        </button>
                    </div>
                </div>
                <div className={styles.list}>
                    {isLoading && !orders.length &&
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={`sk-${i}`} className={styles.row_skeleton}>
                                <div className={styles.skeleton_text_block}>
                                    <Skeleton className={styles.skeleton_line_primary} />
                                    <Skeleton className={styles.skeleton_line_secondary} />
                                </div>
                                <Skeleton className={styles.skeleton_badge} />
                                <Skeleton className={styles.skeleton_amount} />
                            </div>
                        ))}
                    {!isLoading && !!error && orders.length === 0 && (
                        <div className={styles.empty_state}>
                            <div className={styles.empty_icon}>
                                <Icon name="error_outlined" width={26} height={26} />
                            </div>
                            <Typography variant="body3" weight="semibold">
                                Failed to load orders
                            </Typography>
                            <Typography variant="body4" className={styles.empty_desc}>
                                There was a problem fetching your orders.
                            </Typography>
                            <Button
                                variant="outlined-primary"
                                size="small"
                                onClick={() => fetchOrders({ isNew: true })}>
                                Retry
                            </Button>
                        </div>
                    )}
                    {!isLoading && !error && orders.length === 0 && (
                        <div className={styles.empty_state}>
                            <div className={styles.empty_icon}>
                                <Icon name="outlined_wallet" width={26} height={26} />
                            </div>
                            <Typography variant="body3" weight="semibold">
                                No orders yet
                            </Typography>
                            <Typography variant="body4" className={styles.empty_desc}>
                                Orders placed through your integration will appear here.
                            </Typography>
                        </div>
                    )}
                    {orders.map((order) => {
                        return (
                            <div
                                key={order.id}
                                className={[
                                    styles.list_row,
                                    isLoadingDetail ? styles.list_row_disabled : ''
                                ].join(' ')}
                                role="button"
                                tabIndex={0}
                                onClick={() => openDetail(order.id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') openDetail(order.id);
                                }}>
                                <div className={styles.row_order_block}>
                                    <div className={styles.row_order_id_row}>
                                        <span className={styles.row_order_id}>
                                            #{order.order_id}
                                        </span>
                                        {order.environment === 'test' && (
                                            <span className={styles.row_env_badge}>Test</span>
                                        )}
                                    </div>
                                    <p className={styles.row_secondary}>
                                        <span className={styles.row_handle_text}>
                                            @{order.customer_id}
                                        </span>
                                        <span className={styles.row_dot}> · </span>
                                        {formatDate(order.created_at, 'DD MMM YYYY')}
                                    </p>
                                </div>
                                <div className={styles.row_badge}>
                                    <Status variant={orderStatusVariant(order.status)}>
                                        {orderStatusLabel(order.status)}
                                    </Status>
                                </div>
                                <p className={styles.row_amount}>
                                    ${prettifyAmount(String(order.amount))}
                                </p>
                            </div>
                        );
                    })}
                    {isLoading && orders.length > 0 &&
                        Array.from({ length: 3 }).map((_, i) => (
                            <div key={`sk-more-${i}`} className={styles.row_skeleton}>
                                <div className={styles.skeleton_text_block}>
                                    <Skeleton className={styles.skeleton_line_primary} />
                                    <Skeleton className={styles.skeleton_line_secondary} />
                                </div>
                                <Skeleton className={styles.skeleton_badge} />
                                <Skeleton className={styles.skeleton_amount} />
                            </div>
                        ))}
                </div>
            </div>
            <div ref={loader} />
            {isFilterOpen && (
                // Explicit type argument for the same reason as the sales
                // call site — don't rely on JSX to infer TStatus across a
                // mix of covariant/contravariant props.
                <FilterDrawer<string>
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    statusChips={ORDER_STATUS_CHIPS}
                    selectedStatuses={statusChips}
                    sortDir={sortDir}
                    onDateFrom={setDateFrom}
                    onDateTo={setDateTo}
                    onToggleStatus={handleToggleStatus}
                    onSortDir={setSortDir}
                    onReset={handleFilterReset}
                    onClose={handleFilterClose}
                />
            )}
            {selectedRefundEntry && selectedDetail && (
                <RefundDetailDrawer
                    refund={selectedRefundEntry}
                    order={selectedDetail.order}
                    onClose={() => {
                        setSelectedRefundEntry(null);
                        setSelectedDetail(null);
                        detailOpenRef.current = false;
                    }}
                    onViewOrder={() => setSelectedRefundEntry(null)}
                />
            )}
            {selectedTxnEntry && selectedDetail && !selectedRefundEntry && (
                <PurchaseDetailDrawer
                    txn={selectedTxnEntry}
                    order={selectedDetail.order}
                    onClose={() => {
                        setSelectedTxnEntry(null);
                        setSelectedDetail(null);
                        detailOpenRef.current = false;
                    }}
                    onViewOrder={() => setSelectedTxnEntry(null)}
                />
            )}
            {selectedDetail && !selectedRefundEntry && !selectedTxnEntry && (
                <OrderDetailModal
                    detail={selectedDetail}
                    refundLocked={refundJustSucceeded}
                    onClose={() => {
                        setSelectedDetail(null);
                        setIsRefundOpen(false);
                        setRefundJustSucceeded(false);
                        detailOpenRef.current = false;
                    }}
                    onRefundClick={() => setIsRefundOpen(true)}
                    onRefundDetailClick={(refund) => setSelectedRefundEntry(refund)}
                    onTransactionClick={(txn) => {
                        // Mutual exclusivity: opening the txn drawer must close the
                        // refund action modal so both can never mount at once.
                        setIsRefundOpen(false);
                        setSelectedTxnEntry(txn);
                    }}
                />
            )}
            {isRefundOpen && selectedDetail && !selectedRefundEntry && !selectedTxnEntry && (
                <RefundModal
                    order={selectedDetail.order}
                    onClose={() => setIsRefundOpen(false)}
                    onSuccess={handleRefundSuccess}
                />
            )}
        </motion.div>
    );
};

export default DevelopersOrdersLayout;
